import React, { useState, useEffect, useCallback } from "react";
import "./index.scss";
import { Route, Routes, useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  InputField,
  SideMenuAccordion,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import classNames from "classnames";
import Favorite from "./pages/Favorite";
import MyInbox from "./pages/MyInbox";
import Trash from "./pages/Trash";
import HeaderUserDropdown from "./components/HeaderUserDropdown";
import WorkgroupListPage from "./pages/WorkgroupList";
import {
  getAccessToken,
  getAdminInfoFromLocalStorage,
  getUserInfoFromLocalStorage,
} from "./shared/utils/auth";
import WorkgroupAddModal from "./components/WorkgroupAddModal";
import WorkgroupPage from "./pages/Workgroup";
import Notification from "./components/Notification";
import useLoginStore from "./redux/dispatcher/useLoginStore";
import { AlarmRequest } from "./types/common";
import useWorkgroupStore from "./redux/dispatcher/useWorkgroupStore";
import { Workgroup } from "./types/workgroup";
import { LoginDto } from "./types/auth";
import utilsCommon from "./shared/utils/common";
import Search from "./pages/Search";
import docApi from "./redux/api/DocApi";
import { nanoid } from "@reduxjs/toolkit";
import useDocumentStore from "./redux/dispatcher/useDocumentStore";
import AiChatPage from "./components/AIChatPage";
import AIChatIconMenu from "./components/AIChatPage/AIChatIconMenu";
import { DocInfo, UploadFile } from "./types/files";
import { useTranslation } from "react-i18next";
import HomePage from "./pages/Home";
import { PRODUCT_ITEM_TYPE } from "./redux/api/appApi";
import WrapIcon from "./shared/icons/WrapIcon";
import DividerIcon from "./shared/icons/DividerIcon";
import useAppStore from "./redux/dispatcher/useAppStore";
import LinkApp from "./components/LinkApp";
import LinkAppPortal from "./components/LinkApp/LinkAppPortal";
import RootSearchbar from "./components/RootSearchbar";
import { Dummy_tenancyList } from "./DummyOCI";
import WorkspaceAvatar from "./components/WorkspaceAvatar";
import { WorkspaceDTO } from "./redux/store/workspace";

export const SIDE_WORKGROUPLIST_FILTER = {
  isDetail: "Y",
  managerId: "",
  orderBy: "workgroupName",
  orderByType: "ASC",
  pageRange: 1,
  posPage: 1,
  rowRange: 0,
  searchDate: {
    start: 0,
    end: 0,
  },
  status: 1,
  workgroupName: "",
};
function BaseLayout() {
  const toasts = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = getAccessToken() as string | "";
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const { loginState, getAlarmList } = useLoginStore();
  const {
    workgroupState,
    getWorkgroupSideList,
    getWorkspaceConfig,
    getMembersProfile,
  } = useWorkgroupStore();
  const { appState, getAppLoginInfo, uploadDropboxToEco, uploadEcoToDrive } =
    useAppStore();
  const {
    documentState,
    changeDragItem,
    setAddUploadProgressFiles,
    setUpdateUploadProgressFiles,
    completeCheckinByWeb,
    getDocUploadUidCreate,
    doDocUploadFileComplete,
  } = useDocumentStore();
  const [tab, changeTab] = useState("/home");
  const [pageReady, setPageReady] = useState(false);
  const [sideMenuSortby, changeSidemeSortBy] = useState(true);
  const [openExtraModal, changeOpenExtraModal] = useState("CLOSE");
  const [openAiFloatChatPage, toggleOpenFloatAiPage] = useState(false);
  const [dragFiles, changeDragFiles] = useState<DocInfo[]>([]);
  const [targetOpenAppLinkPortal, changeTargetOpenAppLinkPortal] = useState<
    PRODUCT_ITEM_TYPE | undefined
  >();
  const [openAddLinkModal, toggleOpenAddLinkModal] = useState(false);
  const [openFileUploadModal, toggleOpenFileUploadModal] = useState(false);

  useEffect(() => {
    changeTab(window.location.pathname);
    toggleOpenFloatAiPage(false);
    const path = window.location.pathname.split("/");
    if (path.length >= 2) {
      const tabName = window.eco.serviceType !== "OCI" ? path[1] : path[2];
      const workgroupId = window.eco.serviceType !== "OCI" ? path[2] : path[3];
      switch (decodeURI(tabName)) {
        case "":
        case "home":
        case "trash":
        case "favorite":
        case "workgroups":
        case "search":
        case "login":
        case "error":
        case "admin":
          return;
        case "myInbox":
          if (workgroupId && ["sent", "received"].includes(workgroupId)) return;
          window.location.href = `${process.env.PUBLIC_URL}/myInbox/received`;
          return;
        case "workgroup":
          if (
            workgroupState.workgroupSidemenuList?.length > 0 &&
            workgroupState.workgroupSidemenuList.filter(
              (workgroup: Workgroup) => workgroup.workgroupId === workgroupId
            ).length === 0
          ) {
            window.location.href = `${process.env.PUBLIC_URL}/home`;
            toasts.toastMsg(nanoid(), t("common.alert.noneWorkgroup"), "error");
          }
          return;
        default:
          window.location.href = `${process.env.PUBLIC_URL}/error`;
      }
    }
  }, [window.location.pathname, workgroupState.workgroupSidemenuList]);
  useEffect(() => {
    window.addEventListener("dragend", handleDragEnd);
    window.addEventListener("message", receiveMessage);
    getWorkgroupSideList(SIDE_WORKGROUPLIST_FILTER);
    getWorkspaceConfig();
    return () => {
      window.removeEventListener("dragend", handleDragEnd);
      window.removeEventListener("message", receiveMessage);
    };
  }, []);
  useEffect(() => {
    changeDragFiles(documentState.dragItem);
    if (documentState.dragDirectly) {
      toggleOpenFloatAiPage(true);
    }
  }, [documentState.dragItem]);
  useEffect(() => {
    const reqDto: AlarmRequest = { userId: "", logId: "", order: "" };
    if (!token) return;
    else {
      getAlarmList(reqDto);
      setInterval(async () => {
        await getAlarmList(reqDto);
      }, 5 * 60 * 1000); // 5min
    }
  }, [getAlarmList]);
  useEffect(() => {
    if (getUserInfoFromLocalStorage() === null) {
      if (
        getAdminInfoFromLocalStorage() !== null &&
        window.location.href.includes("/admin")
      ) {
        window.location.href = `${process.env.PUBLIC_URL}/admin`;
      } else {
        window.location.href = `${process.env.PUBLIC_URL}/login`;
      }
    } else {
      setPageReady(true);
      const user = getUserInfoFromLocalStorage() as string | "";
      const loginDto: LoginDto = JSON.parse(user);
      if (!loginDto) return;
      getMembersProfile(loginDto?.userInfo?.userId);

      if (window.eco.companyName !== "linkApp") return;
      if (!loginState.appRegisterStatus?.dropbox) {
        getAppLoginInfo("dropbox");
      }
      if (
        utilsCommon.checkMenuAvailable("zoom") &&
        !loginState.appRegisterStatus?.zoom
      ) {
        getAppLoginInfo("zoom");
      }
    }
  }, [getUserInfoFromLocalStorage()]);

  // 브라우저 종료를 통한 웹 리비전 완료 확인 리스너
  const receiveMessage = useCallback(
    async (e: any) => {
      if (!e.data.xFasooSignature || e.data.xFasooSignature !== "Fasoo") return;
      if (e.data.xFasooAction && e.data.xFasooAction.trim !== "") {
        const actionParam = e.data.xFasooActionParam;
        if (e.data.xFasooAction === "endWebEdit") {
          const newSyncInfo = await docApi.getDocSyncInfo({
            workgroupId: actionParam.workgroupId,
            syncId: actionParam.syncId,
          });

          if (
            newSyncInfo &&
            newSyncInfo.revisionNo !== parseInt(actionParam.revisionNo)
          ) {
            toasts.toastMsg(nanoid(), t("file.alert.endInforce"), "error");
          } else {
            // 웹 체크인 api
            toasts.toastMsg(
              nanoid(),
              t("file.alert.completedWebRevise"),
              "success"
            );
            completeCheckinByWeb(
              actionParam.workgroupId,
              actionParam.syncId,
              actionParam.revisionNo
            );
          }

          if (actionParam.workgroupId !== "DUMMYID") {
            setTimeout(() => {
              window.location.reload();
            }, 5000);
          }

          // 페이지 새로고침 추가 필요
        } else if (
          e.data.xFasooAction === "startWebEdit" ||
          e.data.xFasooAction === "fileDelete"
        ) {
          if (actionParam.workgroupId !== "DUMMYID") {
            setTimeout(() => {
              window.location.reload();
            }, 5000);
          }
        }
        // else if (e.data.xFasooAction === "uploadFile") {
        //   changeDragAttachments(actionParam.filter((item: any) => item));
        //   toggleSelectWorkgroupModal(true);
        // }
      }
    },
    [completeCheckinByWeb, toasts]
  );
  /** 문서 테이블 표시 페이지 확인 */
  const isDocPage = (url?: string): boolean => {
    return (
      !!url &&
      ((url?.includes("/workgroup/") && url.includes("/file")) ||
        url?.includes("/myInbox") ||
        url?.includes("/favorite"))
    );
  };
  const onDragStart = (event: any): void => {
    const emptyImg = new Image();
    const dropboxItem = event.currentTarget.closest(
      ".dropbox-item"
    ) as HTMLElement | null;

    emptyImg.src =
      "data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=";
    event.dataTransfer.setDragImage(emptyImg, 0, 0);
    changeDragFiles(
      (dropboxItem?.id ?? event.target.id)
        .substring("appState.dropboxFile:".length)
        .split(", ")
        .filter((item1: string) => item1 !== "")
        .map((item: string) => {
          const file = JSON.parse(item);
          return {
            contentName: file.name,
            size: file.size,
            id: file.id,
          };
        })
    );
  };
  /** UI: 기본 화면 상단 */
  const renderHeader = () => {
    return (
      <Box classes="ecoheader">
        <Box
          classes="cursor pointer"
          onClick={() => navigate(`${process.env.PUBLIC_URL}/home`)}
        >
          <img
            src={utilsCommon.renderWrapsodyECOLogo()}
            alt={"logo"}
            height={32}
            width={129}
          />
        </Box>
        <RootSearchbar />
        <Box classes="mg ml-auto" alignItems="center">
          <Dropdown
            buttonWidth={240}
            style={{ width: "32px", justifyContent: "center" }}
            classes="mg ml-8 ecoheader-service"
            labelNode={<Icons variant="fill" label="questionmark" />}
            align={"right"}
          >
            <DropdownItem
              label={t("login.login.serviceAndDownload")}
              onClick={() => window.open(`${process.env.PUBLIC_URL}/service`)}
            />
          </Dropdown>
          <Dropdown
            width={420}
            buttonWidth={420}
            classes="mg ml-8 ecoheader-notification"
            labelNode={
              <Icons
                variant="fill"
                label="notification"
                isAlert={loginState.alarmResponse?.unreadCount > 0}
              />
            }
            align={"right"}
          >
            <Notification />
          </Dropdown>
          <Box classes="ecoheader-profile mg ml-8">
            <HeaderUserDropdown />
          </Box>
        </Box>
      </Box>
    );
  };
  /** UI: 기본 좌측 사이드 메뉴 */
  const renderSidemenu = () => {
    const tabs = [
      {
        id: "home",
        path: "/home",
        name: t("common.home"),
        icon: <Icons variant="fill" label="home" />,
      },
      {
        id: "fav",
        path: "/favorite",
        name: t("bookmark.title"),
        icon: <Icons variant="fill" label="star" />,
      },
      {
        id: "NONE",
        path: "/myInbox/received",
        name: "My Box",
        icon: <Icons variant="fill" label="user" />,
      },
      {
        id: "/myInbox/received",
        path: "/myInbox/received",
        name: t("sendingFile.received"),
        invisible: !utilsCommon.getServiceConfig(false, "sendFileAvailibility"),
        icon: (
          <Box width={"20px"} classes="mg ml-4" justifyContent="center">
            <Icons variant="fill" label="dot" />
          </Box>
        ),
      },
      {
        id: "/myInbox/sent",
        path: "/myInbox/sent",
        name: t("sendingFile.send"),
        icon: (
          <Box width={"20px"} classes="mg ml-4" justifyContent="center">
            <Icons variant="fill" label="dot" />
          </Box>
        ),
        invisible:
          loginDto?.ecoUserInfo?.userType !== 1 ||
          !utilsCommon.getServiceConfig(false, "sendFileAvailibility"),
        trailingFunction:
          utilsCommon.checkVisibleOptions("sendFile") &&
          ((e: any) => {
            e.stopPropagation();
            changeOpenExtraModal("SEND_FILE");
          }),
      },
      {
        id: "/trash",
        path: "/trash",
        name: t("trash.title"),
        icon: <Icons variant="fill" label="delete" />,
      },
      {
        id: "/workgroups",
        path: "/workgroups",
        name: t("common.workgroup"),
        icon: <Icons variant="fill" label="users" />,
        trailingFunction:
          utilsCommon.checkWorkgroupCreatePermission(loginDto) &&
          ((e: any) => {
            e.stopPropagation();
            changeOpenExtraModal("CREATE_NEW_WORKGROUP");
          }),
      },
    ];

    return (
      <Box classes="sidemenu">
        <Box classes="sidemenu-top overflow-scroll">
          {tabs
            .filter((item1) => !item1.invisible)
            .map((item) => (
              <Box
                classes={classNames("sidemenu-button", {
                  hasIcon: !!item.trailingFunction,
                  selected:
                    (tab.includes(item.id) &&
                      !window.location.pathname.includes("/workgroup/")) ||
                    (tab === "/" && item.id === "home"),
                })}
                onClick={() =>
                  navigate(`${process.env.PUBLIC_URL}${item.path}`)
                }
              >
                {item.icon}
                <Typography
                  classes="mg ml-12"
                  type="body2_rg"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  {item.name}
                </Typography>
                {item.trailingFunction && (
                  <Button
                    btnStyle="icon"
                    size="sm"
                    classes="mg ml-auto"
                    onClick={item.trailingFunction}
                    leadingIcon={<Icons variant="fill" label="plusCircle" />}
                  />
                )}
              </Box>
            ))}
          <SideMenuAccordion
            dropDownDirection
            type="sidemenuNew"
            text={t("common.starrredWorkgroup")}
          >
            {workgroupState.workgroupSidemenuList
              ?.slice()
              ?.filter((item1: Workgroup) => item1.favorite === "Y")
              ?.sort((a: Workgroup, b: Workgroup) =>
                a.workgroupName.localeCompare(b.workgroupName, "en", {
                  numeric: true,
                })
              )

              .map((item: Workgroup) => (
                <Box
                  classes={classNames("sidemenu-button", {
                    selected: tab.includes(item.workgroupId),
                  })}
                  onClick={() =>
                    navigate(
                      `${process.env.PUBLIC_URL}/workgroup/${item.workgroupId}/home`
                    )
                  }
                >
                  <Avatar
                    name={item.workgroupName}
                    size="20"
                    shape="workgroup"
                    uniqueCode={item.workgroupId}
                    opacity={
                      item.isValidate === 0 &&
                      !window.location.href.includes(item.workgroupId)
                        ? "20%"
                        : "100%"
                    }
                  />
                  <Tooltip
                    message={item.workgroupName}
                    maxWidth={"calc(100% - 20px)"}
                  >
                    <Typography
                      classes="mg ml-12"
                      type="body2_rg"
                      exactColor={getColor(
                        window.location.href.includes(item.workgroupId)
                          ? "COLOR_TEXT_INVERSE"
                          : item.isValidate === 0
                          ? "COLOR_TEXT_DISABLED"
                          : "COLOR_TEXT_SECONDARY"
                      )}
                      ellipsis
                    >
                      {item.workgroupName}
                    </Typography>
                  </Tooltip>
                </Box>
              ))}
          </SideMenuAccordion>
          <SideMenuAccordion
            icon={
              <Tooltip
                message={
                  !sideMenuSortby
                    ? t("common.sortDateNameAsc")
                    : t("common.sortDateNameDesc")
                }
              >
                <Button
                  btnStyle="icon"
                  leadingIcon={
                    <Box>
                      <Icons
                        variant="line"
                        label="exchangeUD"
                        stroke={getColor("COLOR_ICON_INFO")}
                      />
                    </Box>
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    getWorkgroupSideList({
                      ...SIDE_WORKGROUPLIST_FILTER,
                      orderByType: sideMenuSortby ? "DESC" : "ASC",
                    });
                    changeSidemeSortBy(!sideMenuSortby);
                  }}
                />
              </Tooltip>
            }
            type="sidemenuNew"
            dropDownDirection
            text={t("common.all")}
          >
            {workgroupState.workgroupSidemenuList
              ?.filter((item1: Workgroup) => item1.favorite)
              ?.map((item: Workgroup) => (
                <Box
                  classes={classNames("sidemenu-button", {
                    selected: tab.includes(item.workgroupId),
                  })}
                  onClick={() =>
                    navigate(
                      `${process.env.PUBLIC_URL}/workgroup/${item.workgroupId}/home`
                    )
                  }
                >
                  <Avatar
                    name={item.workgroupName}
                    size="20"
                    shape="workgroup"
                    uniqueCode={item.workgroupId}
                    opacity={
                      item.isValidate === 0 &&
                      !window.location.href.includes(item.workgroupId)
                        ? "20%"
                        : "100%"
                    }
                  />
                  <Tooltip
                    message={item.workgroupName}
                    maxWidth={"calc(100% - 20px)"}
                  >
                    <Typography
                      classes="mg ml-12"
                      type="body2_rg"
                      exactColor={getColor(
                        window.location.href.includes(item.workgroupId)
                          ? "COLOR_TEXT_INVERSE"
                          : item.isValidate === 0
                          ? "COLOR_TEXT_DISABLED"
                          : "COLOR_TEXT_SECONDARY"
                      )}
                      ellipsis
                    >
                      {item.workgroupName}
                    </Typography>
                  </Tooltip>
                </Box>
              ))}
          </SideMenuAccordion>
        </Box>

        <Box classes="mg mt-auto" justifyContent="center" height={14}>
          <Typography
            fontSize={14}
            lineHeight={"14px"}
            fontWeight={400}
            exactColor={"#717985"}
          >
            © Fasoo
          </Typography>
        </Box>
      </Box>
    );
  };
  const renderSideTenancyList = () => {
    return (
      <Box direction="column" width={48} classes="Wsidemenu-workspaces">
        {Dummy_tenancyList.map((item: WorkspaceDTO) => (
          <WorkspaceAvatar item={item} />
        ))}
      </Box>
    );
  };
  /** 문서 드래그 끝날 경우, 사용 */
  const handleDragEnd = (e: DragEvent): void => {
    e.preventDefault();
    changeDragFiles([]);
  };
  /** UI" 드래그 되고있는 문서 표시 */
  const renderDragItem = (
    subject: string,
    dragItemLength: number
  ): React.ReactNode => {
    return (
      <Box classes="aiChat-dragItems" id={"aiChat-dragItems"}>
        <Icons
          variant="line"
          label="ban"
          id="aiChat-notAllowed"
          classes="aiChat-notAllowed"
          stroke={"#EF5350"}
        />
        <Box classes="aiChat-mainItem">
          <Typography ellipsis lineHeight="normal" type="body2_rg">
            {subject}
          </Typography>
        </Box>
        {dragItemLength > 1 && (
          <>
            <Box classes="aiChat-otherItems " />
            <Box classes="aiChat-number">{dragItemLength}</Box>
          </>
        )}
      </Box>
    );
  };
  const onDragOverFile = (
    event: React.DragEvent<HTMLDivElement>,
    isInbox?: boolean
  ): void => {
    event.preventDefault();
    event.stopPropagation();
    const direction = document.documentElement.dir; // 'ltr' 또는 'rtl'

    const dragItem = document.getElementById("aiChat-dragItems");
    const mouseX = event.clientX; // 마우스의 X 좌표
    const mouseY = event.clientY; // 마우스의 Y 좌표
    if (dragItem && (dragItem.style || (mouseX === 0 && mouseY === 0))) {
      dragItem.style.display = "block";
      dragItem.style.top =
        (mouseY > window.innerHeight / 2 ? mouseY - 48 : mouseY) + "px";
      if (direction === "ltr") {
        dragItem.style.insetInlineStart =
          (mouseX > window.innerWidth / 2 ? mouseX - 240 : mouseX) + "px";
      } else {
        dragItem.style.insetInlineEnd =
          (mouseX < window.innerWidth / 2 ? mouseX + 240 : mouseX) + "px";
      }
    }
    const notAllowedIcon = document.getElementById("aiChat-notAllowed");
    if (isInbox && notAllowedIcon) {
      notAllowedIcon.style.display = "none";
    } else if (notAllowedIcon) {
      notAllowedIcon.style.display = "";
    }
  };
  const handleUploadFilesInUID = async (
    fileList: any[],
    type: "GOOGLE" | "DROPBOX",
    isInBox: boolean,
    ecoToDrivePath?: string
  ): Promise<void> => {
    const uuid = nanoid();
    !isInBox && toggleOpenFileUploadModal(true);
    fileList.forEach(async (targetFile, index) => {
      const file = targetFile;
      const uploadFile: UploadFile = {
        isMultiple: !isInBox,
        uuid: uuid + index,
        fileName: file.contentName,
        fileSize: file.size,
        workgroupId: "",
        workgroupName: "",
        status: "WAIT", // WAIT, ING, SUCCESS, FAIL
        errorMsg: "",
        fileType: type,
      };
      setAddUploadProgressFiles(uploadFile);
    });
    for (const [index, file] of fileList.entries()) {
      let targetUid = "";
      if (!ecoToDrivePath) {
        const res = await getDocUploadUidCreate();
        targetUid = "" + res.payload;
      }
      const fileExt = file.contentName.split(/[.]+/).pop();
      let msg = "";
      let checkExt = true;
      const size = file.size
        ? typeof file.size === "string"
          ? parseInt(file.size)
          : file.size
        : typeof file.fileSize === "string"
        ? parseInt(file.fileSize)
        : file.fileSize;
      setUpdateUploadProgressFiles({
        uuid: uuid + index,
        fileName: file.contentName,
        fileId: file.id,
        fileSize: size,
        workgroupId: "",
        workgroupName: "",
        status: "ING", // WAIT, ING, SUCCESS, FAIL
        fileType: type,
        errorMsg: "",
      });
      if (
        loginDto.serviceConfig.allowedExtensions &&
        loginDto.serviceConfig.allowedExtensions?.length > 0 &&
        !loginDto.serviceConfig.allowedExtensions?.includes(fileExt)
      ) {
        msg = t("file.alert.notSupportedExt"); // by iris
        checkExt = false;
      } else if (
        (!loginDto.serviceConfig.allowedExtensions ||
          loginDto.serviceConfig.allowedExtensions?.length === 0) &&
        loginDto.serviceConfig.disableExtensions.includes(fileExt)
      ) {
        msg = t("file.alert.notSupportedExt"); // by iris
        checkExt = false;
      } else if (file.size === 0) {
        msg = t("file.alert.notAllowZeroSize"); // by iris
        checkExt = false;
      } else if (
        loginDto &&
        size > loginDto.ecoCommonConfig.fileUploadMaxSizeMB * 1024 * 1024
      ) {
        msg = t("file.alert.notAllowMaxSize", {
          num: loginDto.ecoCommonConfig.fileUploadMaxSizeMB,
        });
        checkExt = false;
      }
      let resp: any;
      resp = !ecoToDrivePath
        ? await uploadDropboxToEco({
            uid: targetUid,
            fileId: file.id,
            name: file.contentName,
          })
        : await uploadEcoToDrive({
            type: "dropbox",
            syncId: file.syncId,
            revisionNo: file.revisionNo,
            targetFolder: ecoToDrivePath === "/" ? "" : ecoToDrivePath,
          });
      if (!isInBox || resp.error || ecoToDrivePath)
        setUpdateUploadProgressFiles({
          uuid: uuid + index,
          fileName: file.contentName,
          fileId:
            resp.payload && resp.payload.entity
              ? resp.payload.entity.fileId
              : file.id ?? "",
          fileSize: size,
          workgroupId: "",
          workgroupName: "",
          status: !checkExt || resp.error ? "FAIL" : "SUCCESS", // WAIT, ING, SUCCESS, FAIL
          fileType: type,
          errorMsg: msg ? msg : resp.error ? resp.error.message : "",
        });
      else {
        const workgroupId = window.location.href.split("/")[4] ?? "";
        if (
          resp.error ||
          !workgroupId ||
          !window.location.href.includes("/file") ||
          !!ecoToDrivePath
        )
          return;
        const folderId = window.location.href.split("?folderId=")[1] ?? "";
        const completeResp: any = await doDocUploadFileComplete(targetUid, {
          workgroupId,
          permissionList: [{ changeCode: "CREATE", targetType: "WORKGROUP" }],
          folderId: folderId,
        });
        if (completeResp.payload) {
          setUpdateUploadProgressFiles({
            uuid: uuid + index,
            fileName: file.contentName,
            fileSize: size,
            workgroupId: workgroupId,
            workgroupName: "",
            status: completeResp.payload.error ? "FAIL" : "SUCCESS", // WAIT, ING, SUCCESS, FAIL
            errorMsg: resp ?? completeResp.payload.error?.message ?? "",
          });
        } else if (completeResp?.error) {
          toasts.toastMsg(
            nanoid(),
            completeResp?.error?.message ?? t("common.alert.internalError"),
            "error"
          );
        }
      }
    }
  };
  const onDropFile = (
    event: React.DragEvent<HTMLDivElement>,
    isInBox?: boolean,
    type?: string,
    targetFolder?: string
  ): void => {
    // event.preventDefault();
    if (isInBox) {
      event.stopPropagation();
      if (type === "file" && window.location.href?.includes("/file")) {
        if (!utilsCommon.checkUploadFilePermission(loginDto)) {
          toasts.toastMsg(
            nanoid(),
            t("file.alert.uploadNoPermission"),
            "error"
          );
          return;
        }
        if (!("syncId" in dragFiles[0])) {
          handleUploadFilesInUID(dragFiles as any, "DROPBOX", true);
        }
      } else if (!("syncId" in dragFiles[0])) {
        handleUploadFilesInUID(dragFiles as any, "DROPBOX", false);
      } else if (type === "upload") {
        handleUploadFilesInUID(
          dragFiles as any,
          "DROPBOX",
          true,
          targetFolder ?? "/"
        );
      }
    }
  };
  return pageReady ? (
    <Box
      direction="column"
      height="100%"
      onDragOver={onDragOverFile}
      onDrop={onDropFile}
    >
      {["SEND_FILE", "CREATE_NEW_WORKGROUP"].includes(openExtraModal) && (
        <WorkgroupAddModal
          createType={
            openExtraModal === "SEND_FILE"
              ? "SEND_FILE"
              : "CREATE_NEW_WORKGROUP"
          }
          open
          onClose={() => changeOpenExtraModal("CLOSE")}
        />
      )}
      {openAddLinkModal && (
        <LinkApp
          open={openAddLinkModal}
          onClose={() => toggleOpenAddLinkModal(false)}
          changeTargetOpenAppLinkPortal={changeTargetOpenAppLinkPortal}
        />
      )}
      {openFileUploadModal && (
        <WorkgroupAddModal
          createType={"SEND_FILE"}
          open={openFileUploadModal}
          targetType={"DROPBOX"}
          onClose={() => {
            setUpdateUploadProgressFiles({} as UploadFile);
            toggleOpenFileUploadModal(false);
          }}
        />
      )}
      {renderHeader()}
      {dragFiles?.length > 0 &&
        !documentState.dragDirectly &&
        renderDragItem(dragFiles[0].contentName, dragFiles.length)}
      <Box height={"calc(100% - 61px)"}>
        {window.eco.serviceType === "OCI" && renderSideTenancyList()}
        {renderSidemenu()}
        <Box
          width={
            window.eco.companyName === "linkApp"
              ? "calc(100% - 288px)"
              : "calc(100% - 240px)"
          }
          height={"100%"}
          style={{ position: "relative" }}
        >
          <Routes>
            <Route
              path={`${process.env.PUBLIC_URL}/home`}
              element={<HomePage />}
            />
            <Route path={`${process.env.PUBLIC_URL}`} element={<HomePage />} />
            <Route
              path={`${process.env.PUBLIC_URL}/search`}
              element={<Search />}
            />
            <Route
              path={`${process.env.PUBLIC_URL}/favorite`}
              element={<Favorite />}
            />
            <Route
              id={"received"}
              path={`${process.env.PUBLIC_URL}/myInbox/received`}
              element={<MyInbox tab={0} />}
            />
            <Route
              path={`${process.env.PUBLIC_URL}/myInbox/sent`}
              element={<MyInbox tab={1} />}
            />
            <Route
              path={`${process.env.PUBLIC_URL}/trash`}
              element={<Trash />}
            />
            <Route
              path={`${process.env.PUBLIC_URL}/workgroups`}
              element={<WorkgroupListPage />}
            />
            <Route
              path={`${process.env.PUBLIC_URL}/workgroup/:workgroupId/:tab`}
              element={<WorkgroupPage />}
            />
            <Route
              path={`${process.env.PUBLIC_URL}/workgroup/:workgroupId/*`}
              element={<WorkgroupPage />}
            />
          </Routes>
          <Box classes="uploadFileList"> </Box>
        </Box>
        {!!targetOpenAppLinkPortal && (
          <LinkAppPortal
            target={targetOpenAppLinkPortal}
            open={!!targetOpenAppLinkPortal}
            changeTarget={(item) => changeTargetOpenAppLinkPortal(item)}
            onDragStart={onDragStart}
            onDragOverFile={onDragOverFile}
            onDropFile={onDropFile}
            // handleDeleteDoc={handleDeleteDoc}
            dragFiles={dragFiles}
            // openCreateWorkgroupModal={(type: string) =>
            //   changeWorkgroupAddModalType(type)
            // }
            onClose={() => changeTargetOpenAppLinkPortal(undefined)}
            openCreateWorkgroupModal={(type) => changeOpenExtraModal(type)}
          />
        )}
        {dragFiles?.filter((item) => !item.syncId)?.length > 0 && (
          <div
            className="dragAcceptPage"
            style={{
              width: `calc(100% - ${
                310 +
                (document
                  .getElementById("sideMenuChild2")
                  ?.getBoundingClientRect()?.width
                  ? Number(
                      document
                        .getElementById("sideMenuChild2")
                        ?.getBoundingClientRect()?.width
                    )
                  : 0)
              }px)`,
            }}
            onDragOver={(e) => onDragOverFile(e, true)}
            onDrop={(e) => onDropFile(e, true, "file")}
          >
            <Box direction="column">
              <img
                src={require("./shared/images/pages/File/UploadFile.png")}
                alt={"UploadFile"}
                height={110}
              />
              <Typography
                type="h3"
                textAlign="center"
                exactColor={getColor("COLOR_BG_PRIMARY")}
                classes="mg mt-20"
              >
                {t("file.dropHere")}
              </Typography>
              <Typography
                type="body2_rg"
                textAlign="center"
                exactColor={getColor("COLOR_BG_PRIMARY")}
                classes="mg mt-10"
              >
                {t("account.app.uploadToWorkgroup")}
              </Typography>
            </Box>
          </div>
        )}
        {window.eco.companyName === "linkApp" &&
          targetOpenAppLinkPortal === undefined && (
            <Box
              width={48}
              height={"100%"}
              direction={"column"}
              classes="rightAppLink"
              alignItems="center"
            >
              <WrapIcon />
              <DividerIcon />
              {["googleDrive", "dropbox"]
                .filter((app) => {
                  return (
                    (app === "googleDrive" &&
                      appState.appRegisterStatus.googleDrive) ||
                    (app === "dropbox" && appState.appRegisterStatus.dropbox)
                  );
                })
                .map((item) => (
                  <Button
                    btnStyle="icon"
                    leadingIcon={
                      <img
                        src={require(`./shared/images/logo/${item}20.png`)}
                        alt={item}
                        width={"20px"}
                        height={"20px"}
                      />
                    }
                    onClick={() =>
                      changeTargetOpenAppLinkPortal(item as PRODUCT_ITEM_TYPE)
                    }
                  />
                ))}
              <Button
                onClick={() => toggleOpenAddLinkModal(true)}
                btnStyle="icon"
                leadingIcon={<Icons variant={"fill"} label={"plusCircle"} />}
              />
            </Box>
          )}
      </Box>
      {isDocPage(window.location.href) &&
        (openAiFloatChatPage ? (
          <div
            className="aiChat-float"
            onDragOver={(e) => onDragOverFile(e, true)}
          >
            <AiChatPage
              onClose={() => {
                toggleOpenFloatAiPage(false);
                changeDragItem([]);
              }}
              receivedDragFiles={dragFiles}
              onDropFile={onDropFile}
              // removeFile={removeFile}
            />
          </div>
        ) : (
          utilsCommon.getAIChatAvailibility() === "true" && (
            <div
              className="aiChat-badge"
              onClick={() => toggleOpenFloatAiPage(true)}
              onDragOver={() => {
                if (!openAiFloatChatPage && dragFiles?.length > 0) {
                  toggleOpenFloatAiPage(true);
                }
              }}
            >
              <AIChatIconMenu
                size={"lg"}
                borderRadius={"24px"}
                color={"color"}
              />
            </div>
          )
        ))}
    </Box>
  ) : (
    <Box direction="column" height="100%"></Box>
  );
}

export default BaseLayout;
